import React, { useCallback, useEffect, useState } from 'react';
import { TimeEntry } from '../types/TimeEntry';
import TimeCalendar from './TimeCalendar';
import DayDetailModal from './DayDetailModal';
import AddEntryModal from './AddEntryModal';
import Report from './Report';
import Planning from './Planning';
import CopilotAssistant, { describeSuggestions } from './CopilotAssistant';
import { CopilotStatus } from './CopilotDaySummary';
import { useAuth } from '../contexts/AuthContext';
import { timeRegistrationService } from '../services/TimeRegistrationService';
import './TimeRegistrationApp.css';

const SHAREPOINT_SITE_URL = 'https://igniagroup.sharepoint.com/sites/projects/_layouts/15/viewlsts.aspx?view=14';

type TabType = 'calendar' | 'report' | 'powerbi' | 'planning';
type UserType = 'Ägare' | 'Medarbetare' | 'Extern' | null;

const TimeRegistrationApp: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('calendar');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedEntries, setSelectedEntries] = useState<TimeEntry[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addEntryDate, setAddEntryDate] = useState<string>('');
  const [calendarKey, setCalendarKey] = useState(0); // Force calendar refresh
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [copilotStatus, setCopilotStatus] = useState<CopilotStatus | null>(null);
  const handleCopilotEntriesAdded = useCallback(() => setCalendarKey(prev => prev + 1), []);
  const [userType, setUserType] = useState<UserType>(null);
  const [userTypeChecked, setUserTypeChecked] = useState(false);
  const [currentUserInfo, setCurrentUserInfo] = useState<{ Id: number; Title: string; EMail: string; LoginName: string } | null>(null);
  
  const { isAuthenticated, login, logout, user, isLoading, getAccessToken } = useAuth();

  // /Lists/Users is the source of truth for what a signed-in user can see - a user with no row
  // there is denied access to the app entirely, even if they have SharePoint site access.
  useEffect(() => {
    const checkUserType = async () => {
      if (!isAuthenticated) {
        setUserType(null);
        setUserTypeChecked(false);
        setCurrentUserInfo(null);
        return;
      }

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          setUserType(null);
          return;
        }

        const currentUser = await timeRegistrationService.getCurrentUserInfo(accessToken);
        setCurrentUserInfo(currentUser);
        const type = await timeRegistrationService.getUserType(currentUser.Id, accessToken);
        console.log('[Access] /Lists/Users lookup - SP user Id:', currentUser.Id, 'EMail:', currentUser.EMail, '-> Usertype:', type);
        setUserType(type);
      } catch (error) {
        console.warn('Could not verify user type from /Lists/Users:', error);
        setUserType(null);
      } finally {
        setUserTypeChecked(true);
      }
    };

    checkUserType();
  }, [isAuthenticated, getAccessToken]);

  const isOwnerGroupMember = userType === 'Ägare';
  const isExternalUser = userType === 'Extern';
  // Copilot suggestions are for internal staff only - an unknown user type counts as not internal
  const isInternalUser = userType === 'Ägare' || userType === 'Medarbetare';

  useEffect(() => {
    if (isExternalUser && activeTab === 'planning') {
      setActiveTab('calendar');
    }
  }, [isExternalUser, activeTab]);


  const handleDayClick = (date: string, entries: TimeEntry[]) => {
    // If no entries exist, open add modal instead
    if (entries.length === 0) {
      setAddEntryDate(date);
      setSelectedEntries([]);
      setIsAddModalOpen(true);
    } else {
      setSelectedDate(date);
      setSelectedEntries(entries);
      setIsModalOpen(true);
    }
  };

  const handleAddEntry = (date: string, existingEntries: TimeEntry[]) => {
    setAddEntryDate(date);
    setSelectedEntries(existingEntries);
    setIsAddModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedDate('');
    setSelectedEntries([]);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
    setAddEntryDate('');
  };

  const handleEntriesUpdate = (updatedEntries: TimeEntry[]) => {
    setSelectedEntries(updatedEntries);
    // Force calendar refresh to show updated data
    setCalendarKey(prev => prev + 1);
  };

  const handleEntryAdded = (newEntry: TimeEntry) => {
    // Force calendar refresh to show new data
    setCalendarKey(prev => prev + 1);
  };

  // Shared skeleton shell shown while MSAL auth resolves and while the /Lists/Users role check runs -
  // avoids flashing "Autentiserar..."/"Kontrollerar behörighet..." text before the real UI is ready.
  const renderAppShellSkeleton = () => (
    <div className="time-registration-app">
      <div className="app-header">
        <div className="header-content">
          <div className="app-skeleton-title" />
          <div className="user-section">
            <div className="app-skeleton-block app-skeleton-username" />
          </div>
        </div>
        <div className="tabs">
          <div className="app-skeleton-tab" />
          <div className="app-skeleton-tab" />
          <div className="app-skeleton-tab" />
        </div>
      </div>
      <div className="main-content">
        <div className="app-skeleton-content">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="app-skeleton-block app-skeleton-row" />
          ))}
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    if (isLoading) {
      return renderAppShellSkeleton();
    }

    return (
      <div className="time-registration-app">
        <div className="app-header">
        <div className="header-content">
          <h3 className="app-title">
            <svg width="28" height="28" viewBox="0 0 100 100" style={{verticalAlign: 'middle', marginRight: '8px'}}>
              <defs>
                <linearGradient id="watchGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor: '#ffffff', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#e4e0d5', stopOpacity: 1}} />
                </linearGradient>
                <radialGradient id="screenGlow1" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" style={{stopColor: '#383B3D', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#1c1c1e', stopOpacity: 1}} />
                </radialGradient>
              </defs>
              <rect x="10" y="10" width="80" height="80" rx="18" fill="url(#watchGradient1)"/>
              <rect x="14" y="14" width="72" height="72" rx="16" fill="url(#screenGlow1)"/>
              <circle cx="50" cy="24" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <circle cx="76" cy="50" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <circle cx="50" cy="76" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <circle cx="24" cy="50" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <line x1="50" y1="50" x2="38" y2="32" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" opacity="0.95"/>
              <line x1="50" y1="50" x2="50" y2="28" stroke="#2dd4cf" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="50" cy="50" r="3" fill="#2dd4cf"/>
              <circle cx="50" cy="50" r="1" fill="#383B3D"/>
            </svg>
            Fröken Ur
            <img src="/ignia-logo-white.svg" alt="Ignia" className="ignia-logo" />
          </h3>
          <button onClick={login} className="auth-button sign-in">
              Logga in
            </button>
          </div>
        </div>
        <div className="sign-in-message">
          <p>Logga in med ditt Microsoft 365-konto för att registrera tid</p>
        </div>
      </div>
    );
  }

  if (!userTypeChecked) {
    return renderAppShellSkeleton();
  }

  if (!userType) {
    return (
      <div className="time-registration-app">
        <div className="app-header">
          <div className="header-content">
            <h3 className="app-title">
              Fröken Ur
              <img src="/ignia-logo-white.svg" alt="Ignia" className="ignia-logo" />
            </h3>
            <div className="user-section">
              <span className="user-name">{user?.name || user?.username}</span>
              <button onClick={logout} className="auth-button sign-out">
                Logga ut
              </button>
            </div>
          </div>
        </div>
        <div className="sign-in-message">
          <p>Du saknar behörighet att använda den här applikationen. Kontakta en administratör för att bli tillagd som användare.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="time-registration-app">
      <div className="app-header">
        <div className="header-content">
          <h3 className="app-title">
            <svg width="28" height="28" viewBox="0 0 100 100" style={{verticalAlign: 'middle', marginRight: '8px'}}>
              <defs>
                <linearGradient id="watchGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor: '#ffffff', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#e4e0d5', stopOpacity: 1}} />
                </linearGradient>
                <radialGradient id="screenGlow2" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" style={{stopColor: '#383B3D', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#1c1c1e', stopOpacity: 1}} />
                </radialGradient>
              </defs>
              <rect x="10" y="10" width="80" height="80" rx="18" fill="url(#watchGradient2)"/>
              <rect x="14" y="14" width="72" height="72" rx="16" fill="url(#screenGlow2)"/>
              <circle cx="50" cy="24" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <circle cx="76" cy="50" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <circle cx="50" cy="76" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <circle cx="24" cy="50" r="1.5" fill="#2dd4cf" opacity="0.8"/>
              <line x1="50" y1="50" x2="38" y2="32" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" opacity="0.95"/>
              <line x1="50" y1="50" x2="50" y2="28" stroke="#2dd4cf" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="50" cy="50" r="3" fill="#2dd4cf"/>
              <circle cx="50" cy="50" r="1" fill="#383B3D"/>
            </svg>
            Fröken Ur
            <img src="/ignia-logo-white.svg" alt="Ignia" className="ignia-logo" />
          </h3>
          <div className="user-section">
            {isOwnerGroupMember && (
              <button 
                onClick={() => window.open(SHAREPOINT_SITE_URL, '_blank')}
                className="auth-button admin-button"
                title="Öppna SharePoint-webbplatsen"
              >
                ⚙️ Admin
              </button>
            )}
            <span className="user-name">{user?.name || user?.username}</span>
            <button onClick={logout} className="auth-button sign-out">
              Logga ut
            </button>
          </div>
        </div>
        
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            Tidsregistrering
          </button>
          <button 
            className={`tab ${activeTab === 'report' ? 'active' : ''}`}
            onClick={() => setActiveTab('report')}
          >
            Översikt
          </button>
          {!isExternalUser && (
            <button 
              className={`tab ${activeTab === 'planning' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning')}
            >
              Planering
            </button>
          )}
          {/* Rapport-fliken (PowerBI) dold tillfälligt, kan återinföras vid behov
          <button 
            className={`tab ${activeTab === 'powerbi' ? 'active' : ''}`}
            onClick={() => setActiveTab('powerbi')}
          >
            Rapport
          </button>
          */}
        </div>
      </div>

      <div className="main-content">
        {activeTab === 'calendar' ? (
          <div className="calendar-section">
            <TimeCalendar
              refreshToken={calendarKey}
              onDayClick={handleDayClick}
              onAddEntry={handleAddEntry}
              todayMarker={
                !isInternalUser
                  ? null
                  : copilotStatus?.pendingCount
                  ? {
                      label: `✨ ${describeSuggestions(copilotStatus)}`,
                      title: 'Copilots förslag på tidrapport för idag',
                      onClick: () => setIsCopilotOpen(true),
                    }
                  : copilotStatus?.loading
                    ? { label: '✨ Copilot tänker…', onClick: () => setIsCopilotOpen(true) }
                    : null
              }
            />
            {isInternalUser && (
              <CopilotAssistant
                isOpen={isCopilotOpen}
                onOpenChange={setIsCopilotOpen}
                currentUser={currentUserInfo}
                onEntriesAdded={handleCopilotEntriesAdded}
                onStatusChange={setCopilotStatus}
              />
            )}
          </div>
        ) : activeTab === 'planning' ? (
          <div className="planning-section">
            <Planning />
          </div>
        ) : activeTab === 'report' ? (
          <div className="report-section">
            <Report />
          </div>
        ) : null}
        {/* Rapport-fliken (PowerBI) dold tillfälligt, kan återinföras vid behov
        {activeTab === 'powerbi' && (
          <div className="powerbi-section">
            <iframe title="Tidrapport" width="100%" height="100%" src="https://app.powerbi.com/view?r=eyJrIjoiNjE2Mzg5MGItNWVjOC00MGM4LTlmYjYtMmJhMTExYjMyNzMxIiwidCI6IjEzYTk5Y2I3LWExYmYtNDlhZS05ZjY3LWVmNTk0YjVhZmJmYiIsImMiOjh9&scale=100" frameBorder="0" allowFullScreen={true}></iframe>
          </div>
        )}
        */}
      </div>

      <DayDetailModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        date={selectedDate}
        entries={selectedEntries}
        onEntriesUpdate={handleEntriesUpdate}
        currentUser={currentUserInfo}
      />

      <AddEntryModal
        isOpen={isAddModalOpen}
        onClose={handleAddModalClose}
        date={addEntryDate}
        existingEntries={selectedEntries}
        onEntryAdded={handleEntryAdded}
        currentUser={currentUserInfo}
      />
    </div>
  );
};

export default TimeRegistrationApp;